import { createPortal } from "react-dom";
import { Share2, X } from "lucide-react";

/**
 * RunwayComparisonOverlay
 * -----------------------
 * Portalled inline modal that renders two pinned snapshots side-by-side —
 * emerald A / cyan B — with a delta bar showing headroom + marg-save shifts
 * and up/down/flat trend tint. Uses the same signed SVG URL each pane already
 * exposes so the CPA sees exactly what the user pinned.
 */
export function RunwayComparisonOverlay({ open, pinA, pinB, buildShareBaseUrl, onClose }) {
  if (!open || !pinA || !pinB) return null;
  const dHead = (pinB.headroom || 0) - (pinA.headroom || 0);
  const dSave = (pinB.marginal_savings || 0) - (pinA.marginal_savings || 0);
  const trending = dHead > 0 ? "up" : dHead < 0 ? "down" : "flat";
  const color =
    trending === "up" ? "#10B981" : trending === "down" ? "#f43f5e" : "#94a3b8";
  return createPortal(
    <div
      data-testid="runway-comparison-overlay"
      className="fixed inset-0 z-[9750] flex items-center justify-center px-4 py-6"
      style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="runway-comparison-drawer"
        className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.99))",
          border: "1px solid rgba(16,185,129,0.55)",
          boxShadow:
            "0 30px 60px -18px rgba(16,185,129,0.45), 0 0 40px -20px rgba(6,182,212,0.5)",
          animation: "runway-drawer-in 0.32s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/25">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-emerald-300 flex items-center gap-1.5">
            <Share2 size={11} /> Runway Comparison · A vs B
          </div>
          <button
            onClick={onClose}
            data-testid="runway-comparison-close"
            aria-label="Close comparison"
            className="text-slate-400 hover:text-white p-1 rounded"
          >
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4">
          {[
            { snap: pinA, label: "A", accent: "#10B981" },
            { snap: pinB, label: "B", accent: "#06B6D4" },
          ].map(({ snap, label, accent }) => (
            <div
              key={label}
              data-testid={`runway-comparison-pane-${label.toLowerCase()}`}
              className="rounded-lg overflow-hidden"
              style={{
                background: "rgba(2,6,23,0.55)",
                border: `1px solid ${accent}55`,
              }}
            >
              <div
                className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.28em] flex items-center justify-between"
                style={{
                  background: `${accent}18`,
                  color: accent,
                  borderBottom: `1px solid ${accent}33`,
                }}
              >
                <span>Snapshot {label}</span>
                <span className="text-slate-500 normal-case tracking-normal">
                  {(snap.created_at || "").slice(0, 10)}
                </span>
              </div>
              <img
                src={`${buildShareBaseUrl()}${snap.url}`}
                alt={`Runway snapshot ${label}`}
                data-testid={`runway-comparison-img-${label.toLowerCase()}`}
                className="w-full h-auto block"
                style={{ maxHeight: 220 }}
              />
              <div className="grid grid-cols-3 gap-2 px-3 py-2 text-[10px] font-mono">
                <div>
                  <div className="text-slate-500 uppercase tracking-widest text-[8px]">
                    Headroom
                  </div>
                  <div className="text-white font-semibold">
                    {Math.round(snap.headroom || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-widest text-[8px]">
                    Marg. save
                  </div>
                  <div className="text-cyan-200">
                    {snap.symbol || "$"}
                    {Math.round(snap.marginal_savings || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500 uppercase tracking-widest text-[8px]">
                    Ccy · rate
                  </div>
                  <div className="text-slate-200">
                    {snap.currency} · {Math.round((snap.marginal_rate || 0) * 100)}%
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div
          data-testid="runway-comparison-delta"
          className="mx-4 mb-4 rounded-lg px-4 py-3 text-[11px] font-mono"
          style={{
            background: `linear-gradient(135deg, ${color}22, transparent)`,
            border: `1px solid ${color}55`,
          }}
        >
          <div className="text-[9px] uppercase tracking-[0.28em] mb-1" style={{ color }}>
            Delta · B minus A
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-200">
            <span>
              Headroom{" "}
              <span className="font-semibold" style={{ color }}>
                {dHead >= 0 ? "+" : ""}
                {dHead.toFixed(1)}
              </span>
            </span>
            <span>
              Marg. save{" "}
              <span className="font-semibold" style={{ color }}>
                {pinB.symbol || "$"}
                {dSave >= 0 ? "+" : ""}
                {Math.round(dSave)}
              </span>
            </span>
            <span className="text-slate-400">Trend: {trending}</span>
            {(() => {
              const publicUrl = `${buildShareBaseUrl()}${pinA.url}?compare=${pinB.token}`;
              return (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="runway-comparison-public-link"
                  className="ml-auto text-cyan-300 hover:text-cyan-200 underline decoration-dotted underline-offset-4 text-[10px]"
                  title="Open the public compare view (no login required)"
                >
                  open public compare →
                </a>
              );
            })()}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
